import * as Dialog from "@radix-ui/react-dialog";
import { Bell, BellOff, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePushNotifications } from "@/hooks/usePushNotifications";

interface NotificationOptInSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: () => void;
}

/**
 * Privacy opt-in sheet for push notifications (Phase 13b).
 *
 * Shown after onboarding / first login to ask the user if they want to
 * receive push reminders for recipe feedback and menu notifications.
 * The user can always change their mind in the settings page.
 */
export function NotificationOptInSheet({
  open,
  onOpenChange,
  onComplete,
}: NotificationOptInSheetProps) {
  const { permission, isLoading, error, optIn } = usePushNotifications();

  const handleEnable = async () => {
    await optIn();
    onComplete();
  };

  const handleSkip = () => {
    onComplete();
    onOpenChange(false);
  };

  const handleDismiss = () => {
    onOpenChange(false);
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-foreground/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in" />
        <Dialog.Content className="fixed inset-x-0 bottom-0 z-50 mx-auto max-w-md border-t border-hair bg-background pb-[env(safe-area-inset-bottom)] shadow-soft-warm-lg data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom-4 sm:bottom-auto sm:top-1/2 sm:max-w-lg sm:-translate-y-1/2 sm:border sm:rounded-none">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-hair px-6 py-4">
            <Dialog.Title className="font-serif text-[15px] font-medium">
              Recevoir des notifications ?
            </Dialog.Title>
            <button
              aria-label="Fermer"
              onClick={handleDismiss}
              className="text-foreground/60 hover:text-foreground"
            >
              <X className="size-4" />
            </button>
          </div>

          <div className="space-y-5 px-6 py-5">
            {/* Icon */}
            <div className="flex justify-center">
              <div className="flex size-16 items-center justify-center rounded-full bg-accent/10">
                <Bell className="size-8 text-accent" />
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2 text-center">
              <p className="text-[14px] font-medium text-foreground">
                Ne manquez jamais un retour !
              </p>
              <p className="text-[13px] text-ink-60 leading-relaxed">
                Activez les notifications pour recevoir un rappel amical{" "}
                <strong>24h après chaque repas</strong> et nous donner votre
                avis. Vos retours nous aident à améliorer vos menus de la
                semaine suivante.
              </p>
            </div>

            {/* Privacy note */}
            <div className="rounded-pill border border-hair bg-wash px-4 py-3">
              <div className="flex items-start gap-3">
                <BellOff className="mt-0.5 size-4 shrink-0 text-ink-40" />
                <p className="text-[12px] text-ink-50 leading-relaxed">
                  Vous pouvez à tout moment modifier ce choix dans{" "}
                  <strong>Paramètres &gt; Notifications</strong>. Aucune donnée
                  personnelle n'est partagée avec des tiers.
                </p>
              </div>
            </div>

            {/* Error */}
            {error && (
              <p className="text-center text-[12px] text-destructive">
                {error}
              </p>
            )}

            {/* Permission status */}
            {permission === "denied" && (
              <p className="text-center text-[12px] text-warning">
                Les notifications sont actuellement bloquées par votre
                navigateur. Vous pouvez les réactiver dans les paramètres du
                site.
              </p>
            )}
          </div>

          {/* Footer actions */}
          <div className="flex items-center gap-3 border-t border-hair px-6 py-4">
            <Button
              variant="ghost"
              size="sm"
              className="flex-1"
              onClick={handleSkip}
              disabled={isLoading}
            >
              Plus tard
            </Button>
            <Button
              size="sm"
              className="flex-1"
              onClick={handleEnable}
              disabled={isLoading || permission === "denied"}
            >
              {isLoading ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                "Activer"
              )}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
